import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { ChevronDown, ChevronRight, LogOut, Settings, User } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import SimCollectLogo from "../logo/SimCollectLogo";
import { NAV_GROUPS } from "../../config/dashboardNav";
import { USE_MOCK_AUTH } from "../../config/api";
import { clearOpsDashboardOverviewCache, fetchOpsDashboardOverview } from "../../services/dashboardApi";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";
import { getReportNavChildren } from "../../config/opsReports";
import usePermissions from "../../hooks/usePermissions";
import useRole from "../../hooks/useRole";
import { performLogout } from "../../utils/authSession";
import { showInfo } from "../../utils/toast";
import {
  SIDEBAR_ASIDE,
  SIDEBAR_FOOTER_BORDER,
  SIDEBAR_GROUP_LABEL,
  SIDEBAR_HEADER_BORDER,
  navIconWrapClass,
  navLinkClass,
  subNavLinkClass,
  SECTION_HEADER_ACTIVE,
  SECTION_HEADER_INACTIVE,
  USER_CARD_BUTTON,
  USER_MENU_PANEL,
  USER_MENU_ITEM,
} from "./sidebarTheme";

function pathMatches(pathname, matchPath, itemPath, end) {
  if (matchPath) return pathname === matchPath || pathname.startsWith(`${matchPath}/`);
  if (end) return pathname === itemPath;
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function NavBadge({ count, collapsed }) {
  if (!count) return null;
  const label = count > 99 ? "99+" : String(count);
  if (collapsed) {
    return (
      <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold text-primary">
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[10px] font-bold text-primary">
      {label}
    </span>
  );
}

function NavItem({ item, collapsed, onNavigate }) {
  return (
    <NavLink
      to={item.path}
      end={item.end}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) => navLinkClass({ isActive, collapsed })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-accent shadow-glow"
              aria-hidden
            />
          )}
          <span className={`relative ${navIconWrapClass(isActive)}`}>
            <item.icon className="h-4 w-4" strokeWidth={isActive ? 2.25 : 2} />
            {collapsed ? <NavBadge count={item.badge} collapsed /> : null}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              <NavBadge count={item.badge} />
              {isActive && !item.badge ? (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-accent/80" aria-hidden />
              ) : null}
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

function canSeeNavChild(item, hasAnyPermission) {
  if (!item) return false;
  if (item.permissions?.length && !hasAnyPermission(item.permissions)) return false;
  return true;
}

function filterNavChildren(children, hasAnyPermission) {
  return (children || [])
    .map((child) => {
      if (Array.isArray(child.children) && child.children.length) {
        const nested = filterNavChildren(child.children, hasAnyPermission);
        if (!nested.length) return null;
        if (child.permissions?.length && !hasAnyPermission(child.permissions)) return null;
        return { ...child, children: nested };
      }
      return canSeeNavChild(child, hasAnyPermission) ? child : null;
    })
    .filter(Boolean);
}

function SubNavLink({ item, onNavigate, nested = false }) {
  return (
    <NavLink
      to={item.path}
      end
      onClick={onNavigate}
      title={item.description || item.label}
      className={({ isActive }) => subNavLinkClass(isActive, nested)}
    >
      <span className="line-clamp-2">
        {item.code ? (
          <>
            <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-primary/35">
              {item.code}
            </span>
            {item.shortLabel || item.label}
          </>
        ) : (
          item.shortLabel || item.label
        )}
      </span>
    </NavLink>
  );
}

function NavItemWithChildren({ item, collapsed, onNavigate }) {
  const location = useLocation();
  const { hasAnyPermission } = usePermissions();
  const children = filterNavChildren(item.children, hasAnyPermission);
  const sectionActive = pathMatches(location.pathname, item.matchPath, item.path, item.end);
  const [open, setOpen] = useState(sectionActive);

  useEffect(() => {
    if (sectionActive) setOpen(true);
  }, [sectionActive]);

  if (collapsed) {
    return <NavItem item={item} collapsed onNavigate={onNavigate} />;
  }

  return (
    <div>
      <div
        className={`
          group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium
          transition-all duration-200
          ${sectionActive ? SECTION_HEADER_ACTIVE : SECTION_HEADER_INACTIVE}
        `}
      >
        {sectionActive && (
          <span
            className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-accent shadow-glow"
            aria-hidden
          />
        )}
        <NavLink
          to={item.path}
          onClick={onNavigate}
          className="flex min-w-0 flex-1 items-center gap-3"
        >
          <span
            className={`
              flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors
              ${sectionActive ? "bg-accent/25 text-primary" : "bg-primary/[0.05] text-primary/45"}
            `}
          >
            <item.icon className="h-4 w-4" strokeWidth={sectionActive ? 2.25 : 2} />
          </span>
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
        </NavLink>
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? `Collapse ${item.label}` : `Expand ${item.label}`}
          onClick={() => setOpen((v) => !v)}
          className="rounded-md p-1 text-primary/45 hover:bg-primary/[0.06] hover:text-primary"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
          />
        </button>
      </div>

      {open ? (
        <div className="ml-4 mt-1 space-y-2 border-l border-primary/10 pl-2">
          {children.map((child) => {
            if (Array.isArray(child.children) && child.children.length) {
              return (
                <div key={child.label} className="space-y-0.5">
                  <p className="px-2 pt-1 text-[9px] font-bold uppercase tracking-[0.14em] text-primary/35">
                    {child.label}
                  </p>
                  <ul className="space-y-0.5">
                    {child.children.map((leaf) => (
                      <li key={leaf.path}>
                        <SubNavLink item={leaf} onNavigate={onNavigate} nested />
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
            return (
              <div key={child.path || child.label}>
                <SubNavLink item={child} onNavigate={onNavigate} />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function OpsUserCard({ collapsed, onNavigate }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token } = useSelector((state) => state.auth);
  const { roleLabel } = useRole();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const initials = (user?.name ?? "SC")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (path, state) => {
    setOpen(false);
    onNavigate?.();
    navigate(path, state ? { state } : undefined);
  };

  const handleLogout = async () => {
    setOpen(false);
    onNavigate?.();
    await performLogout(dispatch, token);
    showInfo("You have been signed out.");
    navigate("/login", { replace: true });
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={collapsed ? `${roleLabel} · ${user?.name ?? "Ops user"}` : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`${USER_CARD_BUTTON} ${collapsed ? "flex justify-center p-2" : "p-3 text-left"}`}
      >
        <div className={`flex items-center ${collapsed ? "" : "gap-3"}`}>
          <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-accent to-accent-dark text-xs font-bold text-primary shadow-md">
            {initials}
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-primary bg-emerald-400" />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold uppercase tracking-wide text-accent">
                {roleLabel || "Ops user"}
              </p>
              <p className="mt-0.5 truncate text-sm font-semibold text-primary">
                {user?.name ?? "Ops user"}
              </p>
            </div>
          )}
        </div>
      </button>

      {open ? (
        <div
          role="menu"
          className={`${USER_MENU_PANEL} ${
            collapsed ? "bottom-0 left-[calc(100%+8px)]" : "bottom-[calc(100%+8px)] left-0 right-0 w-auto"
          }`}
        >
          <div className="border-b border-primary/10 px-3 py-2.5">
            <p className="truncate text-xs font-bold uppercase tracking-wide text-accent">
              {roleLabel || "Ops user"}
            </p>
            <p className="mt-0.5 truncate text-sm font-semibold text-primary">{user?.name}</p>
            <p className="truncate text-[11px] text-primary/45">{user?.email}</p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => go("/dashboard/settings", { section: "profile" })}
            className={`mt-1 ${USER_MENU_ITEM}`}
          >
            <User className="h-3.5 w-3.5" />
            Profile
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => go("/dashboard/settings")}
            className={USER_MENU_ITEM}
          >
            <Settings className="h-3.5 w-3.5" />
            Settings
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
          >
            <LogOut className="h-3.5 w-3.5" />
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function Sidebar({ className = "", collapsed = false, onNavigate }) {
  const { user, token } = useSelector((state) => state.auth);
  const { hasAnyPermission } = usePermissions();
  const { roleLabel, isCallCenterAgent, isExecutiveManager, isSuperAdmin } = useRole();
  const reportNavChildren = useMemo(() => getReportNavChildren(user), [user]);
  const [followUpCount, setFollowUpCount] = useState(0);
  const liveTick = useLiveEventTick(/^(portfolio\.|case\.|engagement\.|payment\.)/);

  useEffect(() => {
    if (!token || USE_MOCK_AUTH) return undefined;
    let cancelled = false;
    if (liveTick) clearOpsDashboardOverviewCache();
    fetchOpsDashboardOverview(token)
      .then((data) => {
        if (!cancelled) setFollowUpCount(Number(data?.work?.follow_up_pending || 0));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, liveTick]);

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .map((item) =>
        item.path === "/dashboard/reports"
          ? { ...item, children: reportNavChildren }
          : item.matchPath === "/dashboard/cases"
            ? {
                ...item,
                path: isCallCenterAgent ? "/dashboard/cases/assigned" : "/dashboard/cases/unassigned",
              }
              : item.badgeKey === "follow_ups"
              ? {
                  ...item,
                  badge: followUpCount,
                  description: isExecutiveManager || isSuperAdmin
                    ? "Follow-ups for call centre managers and agents"
                    : item.description,
                }
              : item
      )
      .filter((item) => {
        if (item.path === "/dashboard/reports" && !(item.children?.length)) return false;
        return !item.permissions?.length || hasAnyPermission(item.permissions);
      }),
  })).filter(
    (group) =>
      (!group.permissions?.length || hasAnyPermission(group.permissions)) && group.items.length > 0
  );

  return (
    <aside
      className={`${SIDEBAR_ASIDE} ${collapsed ? "w-[72px]" : "w-[280px]"} ${className}`}
    >
      <span
        className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-accent via-collect to-accent"
        aria-hidden
      />
      <div
        className={`relative shrink-0 overflow-hidden ${SIDEBAR_HEADER_BORDER} ${
          collapsed ? "px-3 py-4" : "px-5 py-5"
        }`}
      >
        <div
          className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-accent/10 blur-2xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -left-6 bottom-0 h-24 w-24 rounded-full bg-collect/15 blur-2xl"
          aria-hidden
        />
        {collapsed ? (
          <div className="flex justify-center">
            <SimCollectLogo theme="light" iconSize={34} variant="icon" />
          </div>
        ) : (
          <>
            <SimCollectLogo theme="light" iconSize={34} variant="wordmark" />
            <div className="mt-3.5 flex items-stretch gap-2.5">
              <span className="w-0.5 shrink-0 rounded-full bg-gradient-to-b from-accent to-collect" aria-hidden />
              <div className="min-w-0">
                <p className="truncate text-sm font-bold tracking-wide text-primary">
                  {roleLabel || "Ops user"}
                </p>
                <p className="mt-0.5 truncate text-[11px] font-medium text-primary/50">
                  Operations portal
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      <nav
        className="dashboard-sidebar-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-4"
        aria-label="Dashboard navigation"
      >
        <div className="space-y-6">
          {visibleGroups.map((group) => (
            <div key={group.label}>
              {!collapsed && (
                <p className={SIDEBAR_GROUP_LABEL}>{group.label}</p>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.path}>
                    {item.children?.length ? (
                      <NavItemWithChildren item={item} collapsed={collapsed} onNavigate={onNavigate} />
                    ) : (
                      <NavItem item={item} collapsed={collapsed} onNavigate={onNavigate} />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="h-4" aria-hidden />
      </nav>

      <div className={`shrink-0 ${SIDEBAR_FOOTER_BORDER} ${collapsed ? "p-2" : "p-4"}`}>
        <OpsUserCard collapsed={collapsed} onNavigate={onNavigate} />
      </div>
    </aside>
  );
}
